// Handing the user a file, in both runtimes (TODO §14.7.4).
//
// On the web there is one way to do this: an `<a download>` click, which lands the file in
// the browser's download folder with no say from the user. In the desktop shell that reads
// as a bug — a desktop app that "exports" by silently dropping something in ~/Downloads is a
// website wearing a window frame — so the shell puts up the OS save panel instead
// (`shell:save-file`, `electron/main.ts`).
//
// Callers do not branch: they hand over bytes and a suggested name, and the runtime decides.
// A cancelled save panel is not an error — the user changed their mind — so the result says
// whether a file was actually written, and nobody has to guess from an exception.

import { desktopShell } from "@/lib/desktopBridge";

export type SaveResult = "saved" | "cancelled";

/**
 * A deck title turned into a file name for an export. The sanitiser is shared so `deck.pdf`,
 * `deck.html` and `deck.deckai` never disagree about what the same deck is called, and an
 * empty or punctuation-only title still yields something a save panel can accept.
 */
export function exportFileName(title: string, extension: string): string {
  const safe = title
    .trim()
    .replace(/[^\w.-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${safe || "deck"}.${extension}`;
}

async function toBytes(data: Blob | Uint8Array | string): Promise<Uint8Array> {
  if (typeof data === "string") return new TextEncoder().encode(data);
  if (data instanceof Uint8Array) return data;
  return new Uint8Array(await data.arrayBuffer());
}

function browserDownload(
  data: Blob | Uint8Array | string,
  fileName: string,
  mime: string,
): void {
  const blob =
    data instanceof Blob
      ? data
      : new Blob([data instanceof Uint8Array ? new Uint8Array(data) : data], {
          type: mime,
        });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the click-driven navigation has consumed the URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Save `data` as `fileName`. Resolves once the file is written (desktop) or the download has
 * been handed to the browser — which always reports `"saved"`, since a browser download
 * cannot be cancelled from here.
 */
export async function saveFile(
  data: Blob | Uint8Array | string,
  fileName: string,
  mime: string,
): Promise<SaveResult> {
  const shell = desktopShell();
  if (!shell) {
    browserDownload(data, fileName, mime);
    return "saved";
  }
  try {
    const written = await shell.saveFile(fileName, await toBytes(data));
    return written ? "saved" : "cancelled";
  } catch (err) {
    // A shell that fails mid-save must not lose the user their export: fall back to the
    // browser path, which in Electron still works (Chromium downloads it).
    console.warn(
      "[save-file] native save failed; falling back to a download",
      err,
    );
    browserDownload(data, fileName, mime);
    return "saved";
  }
}
